import { logger } from "@/utils/logger";

// Guard at the API layer so every fetch-by-filename caller is protected against
// known bad values that pollute the console with non-failures from the upstream.
//
// Three classes of bad input:
//  1. DB sentinels meaning "no image": "", "geen", "false", "true".
//  2. Non-string values (null/undefined/boolean) — weakly-typed callers sometimes
//     pass these through after a falsy check that misses booleans.
//  3. Corrupted filenames stored by a historical bug — the upstream's own error
//     message was persisted back into sf_scenarios.image, e.g. the literal
//     "false # true or false or filename". Real filenames never contain "#" or
//     whitespace, so we treat either as "no image" rather than re-firing the error.
const NON_IMAGE_FILENAMES = new Set(["", "geen", "false", "true"]);

function isFetchableFilename(filename: unknown): filename is string {
  if (typeof filename !== "string") return false;
  const trimmed = filename.trim();
  if (NON_IMAGE_FILENAMES.has(trimmed)) return false;
  if (/[#\s]/.test(trimmed)) return false;
  return true;
}

export const imagesApi = {
  getActorImageByFilename: async (filename: string) => {
    if (!isFetchableFilename(filename)) return "";
    try {
      // Call proxy route instead of Talks API directly (token hidden on server)
      // No JWT needed - proxy is just forwarding to Talks API
      const response = await fetch("/api/proxy/talks/get-image", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          filename,
          type: "actor",
          expected: "url",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Missing/invalid image is non-fatal: callers treat "" as "no image".
        // Warn (dev-only) instead of throwing so a bad upstream filename does
        // not surface as a console/overlay error.
        logger.warn(
          `getActorImageByFilename: ${errorData.error || "fetch failed"} (${filename})`,
        );
        return "";
      }

      // Parse JSON response to get the URL
      const data = await response.json();
      return data.url;
    } catch (error) {
      logger.warn("getActorImageByFilename failed:", error);
      return "";
    }
  },
  // get-image for onboarding, scenarios, etc. Returns https URL (e.g. Azure Blob Storage).
  // Does NOT return blob: URL — those are created client-side for local user uploads.
  getImage: async (filename: string, type = "onboarding") => {
    if (!isFetchableFilename(filename)) return "";
    try {
      // Call proxy route instead of Talks API directly (token hidden on server)
      // No JWT needed - proxy is just forwarding to Talks API
      const response = await fetch("/api/proxy/talks/get-image", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          filename,
          type,
          expected: "url",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Non-fatal: callers treat "" as "no image". Warn instead of throwing.
        logger.warn(
          `getImage: ${errorData.error || "fetch failed"} (${type}/${filename})`,
        );
        return "";
      }

      // Parse JSON response to get the URL
      const data = await response.json();
      return data.url;
    } catch (error) {
      logger.warn("getImage failed:", error);
      return "";
    }
  },

  // Get scenario image by filename
  getScenarioImage: async (filename: string) => {
    if (!isFetchableFilename(filename)) return "";
    try {
      // Call proxy route instead of Talks API directly (token hidden on server)
      // No JWT needed - proxy is just forwarding to Talks API
      const response = await fetch("/api/proxy/talks/get-image", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          filename,
          type: "scenario",
          expected: "url",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Non-fatal: callers treat "" as "no image". Warn instead of throwing.
        logger.warn(
          `getScenarioImage: ${errorData.error || "fetch failed"} (${filename})`,
        );
        return "";
      }

      // Parse JSON response to get the URL
      const data = await response.json();
      return data.url;
    } catch (error) {
      logger.warn("getScenarioImage failed:", error);
      return "";
    }
  },

  // Get client image by filename (logo or background)
  getClientImage: async (filename: string) => {
    if (!isFetchableFilename(filename)) return "";
    try {
      const response = await fetch("/api/proxy/talks/get-image", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          filename,
          type: "client-image",
          expected: "url",
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Non-fatal: callers treat "" as "no image". Warn instead of throwing.
        logger.warn(
          `getClientImage: ${errorData.error || "fetch failed"} (${filename})`,
        );
        return "";
      }

      // Parse JSON response to get the URL
      const data = await response.json();
      return data.url;
    } catch (error) {
      logger.warn("getClientImage failed:", error);
      return "";
    }
  },

  // Upload actor image
  uploadActorImage: async (
    file: File,
    actorCode: string,
    actorVersion: number,
    gender: string | undefined,
    clientId: number,
    userId: number,
    actorId?: number,
  ): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // Normalize gender: 'male'/'m' -> 'm', 'female'/'f' -> 'f', anything else -> 'x'
      let normalizedGender = "x";
      if (gender) {
        const lowerGender = gender.toLowerCase();
        if (lowerGender === "male" || lowerGender === "m") {
          normalizedGender = "m";
        } else if (lowerGender === "female" || lowerGender === "f") {
          normalizedGender = "f";
        }
      }

      // Build query parameters
      const params = new URLSearchParams({
        type: "actor",
        actor_code: actorCode,
        actor_version: actorVersion.toString(),
        gender: normalizedGender,
        client_id: clientId.toString(),
        user_id: userId.toString(),
        group_id: "0",
        asset_kind: "head",
      });
      if (actorId !== undefined) {
        params.set("actor_id", actorId.toString());
      }

      const response = await fetch(
        `/api/proxy/talks/upload-image?${params.toString()}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.error || "Failed to upload image",
        );
      }

      const data = await response.json();
      // Adjust based on actual API response structure
      return data.filename;
    } catch (error) {
      logger.error("Failed to upload actor image:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to upload actor image");
    }
  },

  // Upload a client image (logos, backgrounds, report template images)
  uploadClientImage: async (
    file: File,
    clientId: number,
    userId: number,
  ): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const params = new URLSearchParams({
        type: "client-image",
        client_id: clientId.toString(),
        user_id: userId.toString(),
        group_id: "0",
      });
      const response = await fetch(
        `/api/proxy/talks/upload-image?${params.toString()}`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.error || "Failed to upload client image",
        );
      }
      const data = await response.json();
      return data.filename;
    } catch (error) {
      logger.error("Failed to upload client image:", error);
      if (error instanceof Error) throw error;
      throw new Error("Failed to upload client image");
    }
  },

  // Delete an image from Azure Blob Storage via Talks API.
  // The Talks API also removes the sf_file_map record — no separate fileMap call needed.
  // entity_type: "onboarding" | "actor" | "scenario"
  deleteImage: async (params: {
    id: number;
    diskfile: string;
    entity_type: string;
  }): Promise<void> => {
    try {
      const response = await fetch("/api/proxy/talks/delete-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.error || "Failed to delete image",
        );
      }
    } catch (error) {
      logger.error("Failed to delete image:", error);
      if (error instanceof Error) throw error;
      throw new Error("Failed to delete image");
    }
  },

  // Upload scenario image
  uploadScenarioImage: async (
    file: File,
    clientId: number,
    userId: number,
    scenarioId?: number,
  ): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const params = new URLSearchParams({
        type: "scenario",
        client_id: clientId.toString(),
        user_id: userId.toString(),
        group_id: "0",
      });
      if (scenarioId !== undefined) {
        params.set("scenario_id", scenarioId.toString());
      }

      const response = await fetch(
        `/api/proxy/talks/upload-image?${params.toString()}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            errorData.error ||
            "Failed to upload scenario image",
        );
      }

      const data = await response.json();
      return data.filename;
    } catch (error) {
      logger.error("Failed to upload scenario image:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to upload scenario image");
    }
  },

  // Upload onboarding image
  uploadOnboardingImage: async (
    file: File,
    {
      scenarioId,
      screenOrder,
      language,
      clientId,
      userId,
    }: {
      scenarioId: number;
      screenOrder: number;
      language: string;
      clientId?: number;
      userId?: number;
    },
  ): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // Build query parameters - onboarding requires scenario_id, screen_order, language
      const params = new URLSearchParams({
        type: "onboarding",
        scenario_id: scenarioId.toString(),
        screen_order: screenOrder.toString(),
        language,
        group_id: "0",
      });

      if (clientId) {
        params.append("client_id", clientId.toString());
      }
      if (userId) {
        params.append("user_id", userId.toString());
      }

      const response = await fetch(
        `/api/proxy/talks/upload-image?${params.toString()}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            errorData.error ||
            "Failed to upload onboarding image",
        );
      }

      const data = await response.json();
      return data.filename;
    } catch (error) {
      logger.error("Failed to upload onboarding image:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to upload onboarding image");
    }
  },
};
